import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
import {
  getUsers,
  createUser,
  // updateUser,
  // updateUserPermissions,
  resetUserPassword,
  deleteUser,
  restoreUser,
  getProjects,
} from '../services/api';
import { logout } from '../utils/auth';
import { openModal } from '../store/slices/modalSlice';
import Navbar from '../components/Navbar';
import { useI18n } from '../i18n/I18nContext';
import styles from './UserManagement.module.css';

function UserManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // const [editingUser, setEditingUser] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });
  // const navigate = useNavigate();

  // 表單狀態
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    can_access_all_projects: false,
    project_ids: [],
  });

  const loadUsers = useCallback(async () => {
    try {
      const response = await getUsers(pagination.page, pagination.per_page, includeDeleted);
      setUsers(response.data.users);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages,
      }));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        window.alert(t('users.alert_load_failed'));
      }
    }
  }, [pagination.page, pagination.per_page, includeDeleted, t]);

  const loadProjects = useCallback(async () => {
    try {
      const response = await getProjects();
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error('載入專案列表失敗:', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadProjects();
  }, [loadUsers, loadProjects]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createUser(formData);
      setShowCreateForm(false);
      resetForm();
      loadUsers();
      window.alert(t('users.alert_created'));
    } catch (err) {
      window.alert(err.response?.data?.error || t('users.alert_create_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm(t('users.confirm_reset_password'))) return;

    try {
      const response = await resetUserPassword(userId);
      const newPassword = response.data.new_password;
      if (newPassword) {
        dispatch(openModal({
          title: t('users.modal_reset_title'),
          message: t('users.modal_reset_message_with_password', { password: newPassword }),
        }));
      } else {
        dispatch(openModal({
          title: t('users.modal_reset_title'),
          message: t('users.modal_reset_message_done'),
        }));
      }
    } catch (err) {
      dispatch(openModal({
        title: t('users.modal_error_title'),
        message: err.response?.data?.error || t('users.modal_reset_failed'),
      }));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('users.confirm_delete'))) return;

    try {
      await deleteUser(userId);
      loadUsers();
      window.alert(t('users.alert_deleted'));
    } catch (err) {
      window.alert(err.response?.data?.error || t('users.alert_delete_failed'));
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await restoreUser(userId);
      loadUsers();
      window.alert(t('users.alert_restored'));
    } catch (err) {
      window.alert(err.response?.data?.error || t('users.alert_restore_failed'));
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      can_access_all_projects: false,
      project_ids: [],
    });
  };

  return (
    <div className={styles.userManagement}>
      <Navbar />
      <div className={styles.userManagementContainer}>
        <div className={styles.userManagementHeader}>
          <h1>{t('users.page_title')}</h1>
        </div>

        <div className={styles.userManagementControls}>
          <label>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                setPagination({ ...pagination, page: 1 });
              }}
            />
            {t('users.show_deleted')}
          </label>
          <button onClick={() => setShowCreateForm(true)} className={styles.createButton}>
            {t('users.add_user')}
          </button>
        </div>

        {showCreateForm && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <h2>{t('users.modal_add_title')}</h2>
              <form onSubmit={handleCreateUser}>
                <div className={styles.formGroup}>
                  <label>{t('users.label_username_req')}</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{t('users.label_email_req')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{t('users.label_password_req')}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="project_permission"
                      checked={formData.can_access_all_projects}
                      onChange={() => setFormData({ ...formData, can_access_all_projects: true })}
                    />
                    <span>{t('users.radio_all_projects')}</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="project_permission"
                      checked={!formData.can_access_all_projects}
                      onChange={() => setFormData({ ...formData, can_access_all_projects: false })}
                    />
                    <span>{t('users.radio_per_project')}</span>
                  </label>
                </div>
                {!formData.can_access_all_projects && (
                  <div className={styles.formGroup}>
                    <label>{t('users.label_pick_projects')}</label>
                    {projects.map((project) => (
                      <label key={project.id}>
                        <input
                          type="checkbox"
                          checked={formData.project_ids.includes(project.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                project_ids: [...formData.project_ids, project.id],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                project_ids: formData.project_ids.filter((id) => id !== project.id),
                              });
                            }
                          }}
                        />
                        {project.name}
                      </label>
                    ))}
                  </div>
                )}
                <div className={styles.formActions}>
                  <button type="submit" disabled={loading}>
                    {loading ? t('users.submit_creating') : t('users.submit_create')}
                  </button>
                  <button type="button" onClick={() => { setShowCreateForm(false); resetForm(); }}>
                    {t('users.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className={styles.usersTable}>
          <table>
            <thead>
              <tr>
                <th>{t('users.th_id')}</th>
                <th>{t('users.th_username')}</th>
                <th>{t('users.th_email')}</th>
                <th>{t('users.th_role')}</th>
                <th>{t('users.th_projects')}</th>
                <th>{t('users.th_status')}</th>
                <th>{t('users.th_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={user.deleted_at ? styles.deleted : undefined}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>{user.can_access_all_projects ? t('users.perm_all') : t('users.perm_per_project')}</td>
                  <td>{user.deleted_at ? t('users.status_deleted') : t('users.status_ok')}</td>
                  <td>
                    {user.deleted_at ? (
                      <button onClick={() => handleRestoreUser(user.id)}>{t('users.restore')}</button>
                    ) : (
                      <>
                        <button onClick={() => handleResetPassword(user.id)}>{t('users.reset_password')}</button>
                        <button onClick={() => handleDeleteUser(user.id)}>{t('users.delete')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default UserManagement;
